import * as React from "react";
import { Plus, X } from "lucide-react";

import { cn } from "@/lib/utils";
import { Combobox } from "@/components/Combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { NetPayableRows, payeeLabel } from "@/components/requests/DetailCard";
import { CurrencyConversionCard } from "@/components/requests/CurrencyConversionCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { PHONE_DIALOG_FOOTER, PHONE_SHEET_DIALOG } from "@/lib/responsive";
import { NetPayableLine, netPayableByCurrency } from "@/lib/advance";
import { CURRENCIES } from "@/lib/countries";
import { buildFxLedger, fetchLedgerFxRates, spansMultipleCurrencies } from "@/lib/fx";
import { fetchRequestLinkedAdvance, fetchTravelRequest } from "../../api";
import { Advance, AdvanceAmount, TravelRequestSummary } from "../../types";

/** One currency Finance is paying in - amount kept as typed, so a cleared
 *  field is "no amount yet", not a silent 0. */
interface PayRow {
  key: number;
  currency: string;
  amountText: string;
}

let payRowKey = 0;
const newRow = (currency: string, amount?: number): PayRow => ({
  key: payRowKey++,
  currency,
  amountText: amount === undefined ? "" : String(amount)
});

const round2 = (n: number) => Math.round(n * 100) / 100;

/** What's left to settle, per currency, as approved: on a multi-currency
 *  request (Reimbursement) or any Fixed Allowance request (which always
 *  folds its allowance in as a synthetic row in the request's own currency -
 *  see below), the reconciliation ledger's net per settlement currency (each
 *  row's saved settlement currency and FX override, as set in Review &
 *  approve) - the exact same figures CurrencyConversionCard's own "Net
 *  payable" tiles show further down this dialog; otherwise
 *  netPayableByCurrency's plain per-currency netting. A ledger row with no
 *  exchange rate stays in its own currency rather than being dropped.
 *
 *  Confirmed live bug this fixes: for Fixed Allowance, this used to always
 *  take the netPayableByCurrency branch (gated to `claimMethod ===
 *  "reimbursement"`), which nets each currency independently with no FX
 *  conversion - so "Amounts to be settled" (and the "Amount paid" row it
 *  prefills) disagreed with the ledger card's own already-converted "Net
 *  payable (INR)" tile, and defaulted "Amount paid" to the wrong currency
 *  entirely (e.g. USD instead of the ledger's INR). */
async function loadSettleLines(
  detail: TravelRequestSummary,
  advanceAmounts: AdvanceAmount[]
): Promise<NetPayableLine[]> {
  const hasAllowance = detail.claimMethod === "fixed_allowance" && !!detail.estimatedAllowanceAmount;
  const multi =
    hasAllowance ||
    (detail.claimMethod === "reimbursement" &&
      spansMultipleCurrencies(detail.expenses, advanceAmounts, detail.currency));
  if (!multi) return netPayableByCurrency(detail, advanceAmounts);

  // Fixed Allowance: fold the allowance into the ledger as a synthetic bill
  // in the request's own currency - same trick CurrencyConversionCard's own
  // ledgerExpenses uses, so this agrees with it exactly.
  const expenses = hasAllowance
    ? [
        ...detail.expenses,
        {
          id: "__allowance__",
          category: "Allowance",
          amount: detail.estimatedAllowanceAmount!,
          currency: detail.currency,
          vendor: null,
          description: null,
          date: detail.departureAt || detail.createdAt,
          blobPath: null,
          filename: null,
          contentType: null,
          uploadedAt: detail.departureAt || detail.createdAt
        }
      ]
    : detail.expenses;

  const currencies = new Set([
    ...expenses.map((e) => e.currency || detail.currency),
    ...advanceAmounts.map((a) => a.currency)
  ]);
  const saved = detail.settlementTargetByCurrency ?? {};
  const targets = new Map([...currencies].map((c) => [c, saved[c] ?? detail.currency]));
  // An Other reimbursement has no departure - its advance converts at the
  // request's created date, as everywhere else the ledger is shown.
  const advanceDate = detail.departureAt || detail.createdAt;
  const rates = await fetchLedgerFxRates(
    expenses, advanceAmounts, detail.currency, targets, advanceDate
  );
  const ledger = buildFxLedger(
    expenses, advanceAmounts, detail.currency, detail.currency,
    targets, rates, new Map(), detail.fxOverrides ?? [], advanceDate
  );
  const net = new Map(ledger.netPayments.map((p) => [p.currency, p.amount]));
  for (const row of ledger.rows) {
    if (row.valuationInTarget === null && row.sameCurrOffset !== 0) {
      net.set(row.currency, (net.get(row.currency) || 0) + row.sameCurrOffset);
    }
  }
  return [...net.entries()]
    .map(([currency, amount]) => ({ currency, amount: round2(amount) }))
    .filter((l) => l.amount !== 0)
    .sort((a, b) => a.currency.localeCompare(b.currency))
    .map((l) => ({
      currency: l.currency,
      amount: Math.abs(l.amount),
      direction: l.amount > 0 ? ("payable" as const) : ("owed" as const)
    }));
}

/** Finance's "Mark settled" confirm for an Approved travel request: a
 *  summary of what's left to settle per currency (as approved, including
 *  any settlement currencies and FX overrides set in Review & approve), then
 *  what Finance actually pays - one row per currency, "+" for another.
 *  Prefilled with the payable lines; sent as settledAmounts (see
 *  requests_api.py's settle_request). */
export function SettleRequestDialog({
  travelRequestId,
  open,
  onOpenChange,
  pending,
  onConfirm
}: {
  travelRequestId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
  onConfirm: (settledAmounts: AdvanceAmount[]) => void;
}) {
  const [detail, setDetail] = React.useState<TravelRequestSummary | null>(null);
  const [linkedAdvance, setLinkedAdvance] = React.useState<Advance | null>(null);
  const [lines, setLines] = React.useState<NetPayableLine[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [loadFailed, setLoadFailed] = React.useState(false);
  const [rows, setRows] = React.useState<PayRow[]>([]);

  React.useEffect(() => {
    if (!open || !travelRequestId) {
      setDetail(null);
      setLinkedAdvance(null);
      setLines([]);
      setRows([]);
      setLoadFailed(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadFailed(false);
    Promise.all([
      fetchTravelRequest(travelRequestId),
      fetchRequestLinkedAdvance(travelRequestId).catch(() => null)
    ])
      .then(async ([loaded, linked]) => {
        const settle = await loadSettleLines(loaded, linked?.amounts ?? []);
        if (cancelled) return;
        setDetail(loaded);
        setLinkedAdvance(linked);
        setLines(settle);
        const payable = settle.filter((l) => l.direction === "payable");
        setRows(
          payable.length > 0
            ? payable.map((l) => newRow(l.currency, l.amount))
            : [newRow(loaded.currency, 0)]
        );
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
        setRows([newRow("")]);
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, travelRequestId]);

  const updateRow = (key: number, patch: Partial<PayRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  // Every row needs a currency (each used once) and an amount of 0 or more.
  const settledAmounts: AdvanceAmount[] | null = (() => {
    const out: AdvanceAmount[] = [];
    const seen = new Set<string>();
    for (const r of rows) {
      const amount = Number(r.amountText);
      if (!r.currency || seen.has(r.currency)) return null;
      if (r.amountText.trim() === "" || !Number.isFinite(amount) || amount < 0) return null;
      seen.add(r.currency);
      out.push({ currency: r.currency, amount });
    }
    return out.length > 0 ? out : null;
  })();

  const used = new Set(rows.map((r) => r.currency));
  const hasLedger = detail ? detail.expenses.length > 0 || !!linkedAdvance : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          PHONE_SHEET_DIALOG,
          "max-h-[85vh] overflow-y-auto",
          // Room for the reconciliation table's min-w-[57.5rem] without a
          // horizontal scroll on laptop/desktop - same as RequestDetailDialog.
          hasLedger && "sm:max-w-2xl lg:max-w-[65rem]"
        )}
      >
        <DialogHeader>
          <DialogTitle>Mark this request as settled?</DialogTitle>
          <DialogDescription>
            Enter what you're paying the employee in each currency. This completes
            the request and can't be undone from here.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {loadFailed && (
          <p className="text-sm text-destructive">
            Couldn't load this request's figures - you can still enter the
            amounts paid, or close and try again.
          </p>
        )}

        {detail && !loading && (
          <div className="rounded-md border bg-muted/40 p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Amounts to be settled
            </p>
            {lines.length > 0 ? (
              <NetPayableRows lines={lines} party={payeeLabel(detail)} />
            ) : (
              <p className="mt-2 text-sm text-muted-foreground">
                Nothing left to settle - the advance covers it exactly.
              </p>
            )}
          </div>
        )}

        {/* The full per-currency breakdown behind the summary above - shown
            for every claim method once there's a bill or an advance to
            reconcile, not just Reimbursement. Read-only: this dialog's own
            "Amount paid" inputs below are the actual settlement action. */}
        {detail && !loading && hasLedger && (
          <CurrencyConversionCard
            travelRequestId={detail.id}
            expenses={detail.expenses}
            advanceAmounts={linkedAdvance?.amounts}
            fxOverrides={detail.fxOverrides}
            settlementTargetByCurrency={detail.settlementTargetByCurrency}
            defaultTargetCurrency={detail.currency}
            departureDate={detail.departureAt || detail.createdAt}
            claimMethod={detail.claimMethod}
            // Fixed Allowance only - without this the card's ledger never
            // folds the allowance into the defaultTargetCurrency row (see
            // CurrencyConversionCard's own ledgerExpenses doc comment), so
            // Finance's own "Net payable"/"Owed by" tile silently fell back
            // to the OLD bills-only calculation here too (this prop was
            // missing entirely from this call, same gap as
            // RequestDetailDialog.tsx/ReviewRequestDialog.tsx).
            estimatedAllowanceAmount={
              detail.claimMethod === "fixed_allowance" ? detail.estimatedAllowanceAmount : undefined
            }
            readOnly
          />
        )}

        {!loading && (detail || loadFailed) && (
          <div className="space-y-2">
            <p className="text-sm font-medium">Amount paid</p>
            {rows.map((row) => (
              <div key={row.key} className="flex items-center gap-2">
                <div className="w-28 shrink-0">
                  <Combobox
                    value={row.currency}
                    onChange={(v) => updateRow(row.key, { currency: v })}
                    options={CURRENCIES.filter(
                      (c) => c === row.currency || !used.has(c)
                    ).map((c) => ({ value: c, label: c }))}
                    placeholder="Currency…"
                    searchPlaceholder="Search…"
                  />
                </div>
                <Input
                  type="number"
                  min={0}
                  inputMode="decimal"
                  value={row.amountText}
                  onChange={(e) => updateRow(row.key, { amountText: e.target.value })}
                  onWheel={(e) => e.currentTarget.blur()}
                  placeholder="Amount"
                  aria-label={`Amount paid${row.currency ? ` in ${row.currency}` : ""}`}
                />
                {rows.length > 1 && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="shrink-0"
                    aria-label="Remove this currency"
                    onClick={() => setRows((prev) => prev.filter((r) => r.key !== row.key))}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setRows((prev) => [...prev, newRow("")])}
            >
            <Plus className="mr-1 h-4 w-4" />
            Add currency
          </Button>
        </div>
        )}

        <DialogFooter className={PHONE_DIALOG_FOOTER}>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => settledAmounts && onConfirm(settledAmounts)}
            disabled={pending || loading || settledAmounts === null}
          >
            {pending ? "Saving…" : "Mark settled"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
